import { allDrawingModes, PuzzleDefinition } from "../../types/puzzle/PuzzleDefinition";
import { NumberPTM } from "../../types/puzzle/PuzzleTypeMap";
import { LanguageCode } from "../../types/translations/LanguageCode";
import { RulesParagraph } from "../../components/puzzle/rules/RulesParagraph";
import { GridSize6, Regions6 } from "../../types/puzzle/GridSize";
import { DigitPuzzleTypeManager } from "../../puzzleTypes/default/types/DigitPuzzleTypeManager";
import { Chameleon } from "../authors";
import { observer } from "mobx-react-lite";
import { textColor } from "../../components/app/globals";
import { profiler } from "../../utils/profiler";
import { AutoSvg } from "../../components/svg/auto-svg/AutoSvg";
import { DigitProps } from "../../components/puzzle/digit/DigitProps";
import { DigitComponentType } from "../../components/puzzle/digit/DigitComponentType";
import { ReactNode } from "react";
import { BaseNeighborsConstraint } from "../../components/puzzle/constraints/consecutive-neighbors/ConsecutiveNeighbors";
import { isValidFinishedPuzzleByConstraints } from "../../types/puzzle/Constraint";

// region Custom digit
const AnimalDigit = observer(function AnimalDigit({
    context,
    digit,
    size,
    color = textColor,
    ...containerProps
}: DigitProps<NumberPTM>) {
    profiler.trace();

    return (
        <AutoSvg width={size} height={size} {...containerProps}>
            <AnimalDigitSvgContent context={context} digit={digit} size={size} color={color} />
        </AutoSvg>
    );
});

const svgMap: Record<number, ReactNode> = {
    1: (
        <>
            <path
                fill="currentColor"
                d="M379.987,204.198c-2.456-7.501-4.813-14.566-6.504-21.254c-1.703-6.702-2.74-12.982-2.734-19.03
		c0.014-6.464,1.116-12.664,4.028-19.346c8.406-19.214,11.436-38.125,11.43-55.186c-0.007-21.895-4.92-40.806-9.799-54.335
		c-4.893-13.528-9.785-21.703-10.05-22.146L358.588,0l-63.354,80.633h-78.25L153.637,0.007l-7.771,12.889
		c-0.264,0.449-5.157,8.624-10.049,22.152c-4.886,13.529-9.792,32.426-9.799,54.335c-0.006,17.061,3.024,35.972,11.43,55.179
		c2.806,6.438,3.928,12.816,3.935,19.604c0.052,12.863-4.371,27.25-9.323,42.582c-4.873,15.352-10.268,31.647-10.314,49.23
		c0,4.483,0.37,9.052,1.222,13.641c2.311,12.433,6.96,27.554,12.631,43.77c8.504,24.285,19.34,50.881,28.029,72.34
		c4.338,10.717,8.148,20.152,10.809,27.25c1.327,3.533,2.37,6.491,3.018,8.59c0.323,1.043,0.554,1.876,0.66,2.351l0.026,0.132
		c-0.013,0.694-0.079,1.81-0.211,3.275c-0.436,5.183-1.433,13.523-1.44,22.72c0.013,7.171,0.588,14.936,2.753,22.602
		c2.153,7.62,6.055,15.358,12.935,21.254c5.956,5.084,12.908,8.432,19.907,10.928c10.518,3.698,21.327,5.401,29.646,6.286
		c8.313,0.872,14.117,0.884,14.381,0.884c0.357-0.013,10.538,0,23.427-2.08c6.444-1.056,13.576-2.622,20.594-5.091
		c7.005-2.496,13.952-5.844,19.914-10.928c6.874-5.896,10.776-13.634,12.935-21.254c2.166-7.666,2.74-15.438,2.746-22.608
		c0-6.141-0.449-11.859-0.872-16.56c-0.211-2.35-0.422-4.444-0.561-6.154c-0.132-1.453-0.198-2.549-0.211-3.249
		c0.086-0.382,0.27-1.129,0.561-2.06c0.759-2.522,2.159-6.47,3.981-11.271c6.398-16.89,17.98-44.575,28.458-71.646
		c5.242-13.543,10.215-26.966,14.169-38.97c3.956-12.044,6.907-22.502,8.082-31.006c0.634-4.676,0.918-9.238,0.918-13.675
		C390.241,236.03,384.866,219.186,379.987,204.198z M213.636,101.426h85.618c0.31,0.515,0.614,1.057,0.918,1.664
		c2.12,4.278,3.711,10.591,4.609,17.378c0.924,6.788,1.228,14.05,1.228,20.482c0,4.186-0.125,8.009-0.284,11.146l-18.21-3.038
		l-20,43.314c-14.864-7.844-23.83-18.521-29.343-27.791c-3.136-5.289-5.098-10.102-6.253-13.523
		c-0.574-1.703-0.951-3.063-1.182-3.954l-0.224-0.978l-0.039-0.198l-1.757-9.613l-25.711,11.02
		c-0.819-3.117-1.539-7.138-1.532-11.898C201.52,126.305,203.944,114.638,213.636,101.426z M368.736,266.337
		c-0.495,3.704-1.704,9.006-3.48,15.186c-6.193,21.763-19.036,54.255-30.287,82.185c-5.632,13.991-10.875,26.88-14.757,36.982
		c-1.941,5.052-3.546,9.396-4.721,12.955c-0.588,1.796-1.076,3.381-1.459,4.886c-0.185,0.766-0.357,1.492-0.489,2.284
		c-0.132,0.805-0.258,1.638-0.264,2.832c0.007,1.776,0.133,3.513,0.298,5.448c0.495,5.725,1.366,13.232,1.366,20.944
		c0.007,5.982-0.548,11.99-1.961,16.929c-1.42,4.978-3.546,8.65-6.471,11.159c-3.129,2.707-7.877,5.203-13.297,7.091
		c-8.122,2.885-17.597,4.47-24.9,5.229c-3.651,0.384-6.774,0.575-8.94,0.674c-1.083,0.04-1.935,0.066-2.496,0.08l-0.621,0.013
		h-0.145h-0.046c-0.792,0-10.195-0.125-21.103-1.994c-5.447-0.924-11.257-2.298-16.461-4.173c-5.21-1.875-9.752-4.298-12.796-6.926
		c-2.919-2.502-5.045-6.174-6.471-11.152c-1.406-4.932-1.968-10.948-1.954-16.923c0-5.13,0.383-10.214,0.786-14.691
		c0.205-2.245,0.416-4.338,0.574-6.26c0.166-1.934,0.298-3.658,0.304-5.448c-0.014-1.287-0.152-2.192-0.304-3.076
		c-0.304-1.664-0.733-3.216-1.28-4.998c-2.034-6.55-5.85-16.269-10.703-28.346c-7.257-18.045-16.738-41.01-25.091-63.248
		c-8.345-22.185-15.542-43.842-18.157-58.13c-0.601-3.242-0.872-6.524-0.872-9.871c-0.053-13.093,4.371-27.566,9.323-42.886
		c4.86-15.338,10.261-31.522,10.314-48.926c0.007-9.138-1.604-18.626-5.685-27.929c-7.124-16.296-9.68-32.209-9.686-46.854
		c-0.006-18.758,4.265-35.405,8.564-47.269c0.535-1.486,1.07-2.899,1.598-4.226l43.407,55.252
		c-11.066,15.484-14.533,30.478-14.487,42.298c0.026,15.418,5.454,25.341,5.844,26.074l3.42,6.154l22.687-9.719
		c1.473,4.04,3.645,9.046,6.92,14.579c7.547,12.796,20.99,28.062,43.552,37.438l6.932,2.892l21.44-46.457l23.097,3.843l0.865-8.241
		c0.012-0.172,0.957-9.178,0.957-21.05c-0.007-9.231-0.548-20.152-2.688-30.293c-1.082-5.071-2.562-9.964-4.747-14.434
		c-0.594-1.202-1.255-2.384-1.968-3.526l43.05-54.79c4.734,11.945,10.221,30.36,10.168,51.476c0,14.638-2.556,30.544-9.687,46.854
		c-4.113,9.362-5.783,18.732-5.764,27.678c0,8.399,1.406,16.408,3.361,24.133c2.951,11.595,7.124,22.615,10.406,33.688
		c3.302,11.073,5.738,22.092,5.738,33.714C369.469,258.994,369.244,262.613,368.736,266.337z"
            />
            <path
                fill="currentColor"
                d="M228.176,441.839c-4.305,0-7.798,6.398-7.798,14.288c0,7.896,3.493,14.295,7.798,14.295
		c4.305,0,7.798-6.398,7.798-14.295C235.974,448.237,232.481,441.839,228.176,441.839z"
            />
            <path
                fill="currentColor"
                d="M284.049,441.839c-4.305,0-7.798,6.398-7.798,14.288c0,7.896,3.493,14.295,7.798,14.295
		c4.305,0,7.791-6.398,7.791-14.295C291.84,448.237,288.354,441.839,284.049,441.839z"
            />
            <path
                fill="currentColor"
                d="M180.781,242.726c-8.075,0-14.618,6.537-14.618,14.612c0,8.082,6.543,14.619,14.618,14.619
		c8.076,0,14.612-6.537,14.612-14.619C195.393,249.262,188.856,242.726,180.781,242.726z"
            />
            <path
                fill="currentColor"
                d="M331.443,242.726c-8.075,0-14.612,6.537-14.612,14.612c0,8.082,6.537,14.619,14.612,14.619
		c8.076,0,14.619-6.537,14.619-14.619C346.062,249.262,339.519,242.726,331.443,242.726z"
            />
        </>
    ),
    2: (
        <g transform="scale(2)">
            <path
                fill="currentColor"
                d="M100,140a8,8,0,1,1-8-8A8.00009,8.00009,0,0,1,100,140Zm64,8a8,8,0,1,0-8-8A8.00009,8.00009,0,0,0,164,148Zm67.0752-7.68408a12.19645,12.19645,0,0,1-5.043,1.10742,11.83693,11.83693,0,0,1-9.34766-4.62305L212,130.83813V184a36.04061,36.04061,0,0,1-36,36H80a36.04061,36.04061,0,0,1-36-36V130.83813l-4.68457,5.96216a11.83839,11.83839,0,0,1-9.34766,4.62305,12.19645,12.19645,0,0,1-5.043-1.10742A11.82706,11.82706,0,0,1,18.085,127.17529l16.418-87.5664A12.00119,12.00119,0,0,1,49.209,30.1792L104.49121,44h47.01758L206.792,30.1792a12.001,12.001,0,0,1,14.70508,9.4292l16.418,87.5664A11.82738,11.82738,0,0,1,231.0752,140.31592ZM97.04,50.3833,47.26855,37.93994a4.04357,4.04357,0,0,0-.98144-.12158,4.005,4.005,0,0,0-3.9209,3.26562l-16.418,87.56543a3.99987,3.99987,0,0,0,7.07617,3.2085ZM204,120.65625,150.05566,52H105.94434L52,120.65625V184a28.03146,28.03146,0,0,0,28,28h44V193.65674l-14.82812-14.82813a3.99991,3.99991,0,0,1,5.65624-5.65722L128,186.34326l13.17188-13.17187a3.99991,3.99991,0,0,1,5.65624,5.65722L132,193.65674V212h44a28.03146,28.03146,0,0,0,28-28Zm26.05176,7.99268-16.418-87.56543a3.99966,3.99966,0,0,0-4.90137-3.14356L158.96,50.3833l64.01563,81.47461a3.99993,3.99993,0,0,0,7.07617-3.209Z"
            />
        </g>
    ),
    3: (
        <>
            <path
                fill="currentColor"
                d="M430.762,126.907c-42.384-89.329-67.753-89.329-78.722-89.329c-1.947,0-3.884,0.167-5.785,0.482
    c-18.1,3.014-41.374,22.031-69.302,56.579h-20.955h-20.974c-27.909-34.548-51.202-53.566-69.301-56.579
    c-1.891-0.315-3.829-0.482-5.758-0.482h-0.25c-11.108,0-36.412,0.649-78.499,89.329C34.188,164.608-32.49,256.506,18.091,366.094
    c49.996,108.328,191.425,108.328,237.907,108.328c46.472,0,187.911,0,237.907-108.318
    C544.495,256.506,477.809,164.608,430.762,126.907z M468.055,354.161c-41.15,89.134-161.411,91.776-212.056,91.776
    c-50.626,0-170.906-2.642-212.038-91.776c-56.969-123.432,60.121-208.886,60.121-208.886s36.551-79.213,55.875-79.213l1.094,0.093
    c18.99,3.162,60.14,56.969,60.14,56.969h34.808h34.808c0,0,41.15-53.807,60.14-56.969l1.094-0.093
    c19.305,0,55.875,79.213,55.875,79.213S525.024,230.729,468.055,354.161z"
            />
            <polygon fill="currentColor" points="78.008,314.355 181.746,322.524 182.488,313.057 78.75,304.897 	" />
            <polygon fill="currentColor" points="76.2,355.496 77.21,364.935 178.445,354.133 177.434,344.693 	" />
            <polygon fill="currentColor" points="330.269,322.524 433.988,314.355 433.247,304.897 329.527,313.057 	" />
            <polygon fill="currentColor" points="333.56,354.133 434.796,364.935 435.806,355.496 334.571,344.693 	" />
            <path
                fill="currentColor"
                d="M317.222,368.467c-7.983,1.91-14.715,2.708-20.324,2.708c-7.446,0-12.832-1.391-16.866-3.422
    c-5.981-3.059-9.458-7.621-11.841-13.5c-2.327-5.832-3.171-12.851-3.162-19.157c0-3.95,0.333-7.427,0.695-10.329l16.718-16.718
    c3.078-3.079,4.005-7.714,2.336-11.73c-1.669-4.024-5.582-6.648-9.94-6.648h-37.683c-4.45,0-8.437,2.745-10.032,6.899
    c-1.614,4.154-0.482,8.864,2.819,11.85l16.096,14.576c0.464,3.115,0.927,7.325,0.918,12.118c0.045,8.438-1.531,18.016-5.954,24.581
    c-2.216,3.32-5.016,6.018-9.04,8.057c-4.025,2.031-9.42,3.422-16.857,3.422c-5.601,0-12.341-0.798-20.326-2.716
    c-4.339-1.039-8.697,1.641-9.736,5.971c-1.029,4.339,1.651,8.697,5.981,9.736c8.976,2.142,16.941,3.143,24.081,3.152
    c9.448,0,17.496-1.808,24.126-5.146c7.779-3.895,13.222-9.866,16.857-16.347c0.52,0.918,0.853,1.864,1.428,2.763
    c3.551,5.387,8.623,10.246,15.243,13.584c6.639,3.338,14.688,5.156,24.136,5.146c7.14,0,15.104-1.01,24.08-3.162
    c4.339-1.038,7.009-5.387,5.972-9.726C325.911,370.1,321.553,367.43,317.222,368.467z"
            />
            <polygon fill="currentColor" points="152.742,105.711 138.5,141.316 174.106,134.195 	" />
            <polygon fill="currentColor" points="359.254,105.711 337.891,134.195 373.496,141.316 	" />
            <path
                fill="currentColor"
                d="M146.011,241.809c0,13.983,11.349,25.323,25.332,25.323c13.982,0,25.313-11.34,25.313-25.323
    c0-13.983-11.331-25.322-25.313-25.322C157.36,216.487,146.011,227.826,146.011,241.809z"
            />
            <path
                fill="currentColor"
                d="M340.663,267.132c13.982,0,25.323-11.34,25.323-25.323c0-13.983-11.341-25.322-25.323-25.322
    c-13.983,0-25.322,11.34-25.322,25.322C315.341,255.792,326.68,267.132,340.663,267.132z"
            />
        </>
    ),
    4: (
        <>
            <path
                fill="currentColor"
                d="M387.255,14.077c-67.996,0-123.172,54.422-124.606,122.082h-13.403
		c-2.514-66.686-57.208-119.987-124.501-119.996C55.848,16.172,0.005,71.992,0,140.894c0.024,61.933,45.17,113.102,104.35,122.865
		l106.8,151.262l0.024-0.019c2.288,3.438,4.973,6.528,7.979,9.23c-0.823,3.125-1.402,6.343-1.402,9.717
		c0,21.125,17.126,38.252,38.252,38.252c21.12,0,38.248-17.127,38.248-38.252c0-3.374-0.58-6.592-1.398-9.708
		c3.007-2.711,5.695-5.801,7.99-9.239l0.022,0.019l108.509-153.689C467.69,250.861,511.978,200.152,512,138.816
		C511.995,69.915,456.148,14.077,387.255,14.077z M400.631,238.074l-5.295,0.698L280.718,401.111l-0.129,0.202
		c-0.473,0.744-1.213,1.37-1.792,2.068c-6.381-4.771-14.218-7.695-22.794-7.695c-8.578,0-16.414,2.924-22.794,7.695
		c-0.575-0.698-1.31-1.314-1.779-2.059l-0.138-0.221L118.259,241.025l-5.41-0.635c-49.734-5.883-88.4-48.171-88.376-99.496
		c0.008-27.726,11.206-52.712,29.371-70.896c18.184-18.156,43.175-29.354,70.901-29.363c27.726,0.009,52.712,11.207,70.897,29.363
		c18.165,18.184,29.362,43.17,29.372,70.896c0.005,1.83-0.175,4.036-0.354,6.711l-0.842,13.026h64.733l-1.122-13.266
		c-0.257-3.024-0.44-5.855-0.44-8.55c0.008-27.726,11.206-52.722,29.371-70.905c18.184-18.156,43.17-29.354,70.896-29.362
		c27.727,0.008,52.717,11.206,70.902,29.362c18.166,18.183,29.362,43.179,29.372,70.905
		C487.551,189.636,449.644,231.519,400.631,238.074z"
            />
            <polygon fill="currentColor" points="95.823,395.034 192.166,433.976 197.811,420.002 101.469,381.07 	" />

            <rect
                x="139.285"
                y="422.574"
                transform="matrix(-0.335 -0.9422 0.9422 -0.335 -250.3301 770.7174)"
                fill="currentColor"
                width="15.064"
                height="102.251"
            />

            <rect
                x="313.238"
                y="463.829"
                transform="matrix(-0.9272 -0.3746 0.3746 -0.9272 527.1981 1045.2096)"
                fill="currentColor"
                width="103.9"
                height="15.071"
            />

            <rect
                x="314.065"
                y="397.656"
                transform="matrix(0.9422 -0.3351 0.3351 0.9422 -114.6626 145.798)"
                fill="currentColor"
                width="102.245"
                height="15.065"
            />
            <path
                fill="currentColor"
                d="M124.745,90.268c-27.96,0-50.626,22.661-50.626,50.626c0,27.957,22.665,50.635,50.626,50.635
		s50.626-22.678,50.626-50.635C175.37,112.929,152.705,90.268,124.745,90.268z"
            />
            <path
                fill="currentColor"
                d="M387.255,88.181c-27.965,0-50.63,22.679-50.63,50.635c0,27.965,22.665,50.627,50.63,50.627
		c27.961,0,50.626-22.662,50.626-50.627C437.882,110.86,415.216,88.181,387.255,88.181z"
            />
            <path
                fill="currentColor"
                d="M204.582,274.588c-9.157,0-16.589,7.427-16.589,16.584s7.432,16.584,16.589,16.584
		c9.156,0,16.588-7.427,16.588-16.584S213.738,274.588,204.582,274.588z"
            />
            <path
                fill="currentColor"
                d="M307.424,274.588c-9.175,0-16.603,7.427-16.603,16.584s7.428,16.584,16.603,16.584
		c9.156,0,16.584-7.427,16.584-16.584S316.58,274.588,307.424,274.588z"
            />
        </>
    ),
    5: (
        <>
            <path
                fill="currentColor"
                d="M505.769,201.945l-37.757-77.484c-9.378-19.245-23.893-35.516-41.973-47.052c-18.584-11.858-40.109-18.125-62.249-18.125
    c-12.324,0-24.459,1.926-36.074,5.727c-1.511,0.495-3.026,1.025-4.499,1.577c-4.181,1.565-6.302,6.223-4.738,10.405
    c1.565,4.182,6.223,6.305,10.405,4.738c1.266-0.473,2.565-0.929,3.862-1.355c9.988-3.267,20.432-4.924,31.044-4.924
    c38.462,0,72.828,21.492,89.687,56.09l37.757,77.484c3.05,6.263,4.598,13,4.598,20.025c0,11.24-4.11,22.051-11.573,30.438
    l-51.17,57.511c-5.217,5.862-12.219,9.751-19.819,11.159c4.49-7.765,7.506-16.542,9.066-26.431
    c3.178-20.156-0.157-42.734-4.237-64.131c-0.835-4.386-5.071-7.265-9.454-6.427c-4.386,0.835-7.263,5.068-6.427,9.454
    c7.734,40.576,11.724,78.743-23.921,99.84c-0.94,0.557-1.885,1.11-2.854,1.636c-8.135,4.404-16.622,8.369-25.328,11.847
    c-0.241-4.277-0.345-8.699-0.295-13.128c0.086-4.551,0.344-9.348,0.769-14.255c0.684-7.924-1.77-15.637-6.907-21.719
    c-5.131-6.074-12.327-9.793-20.26-10.473c-16.393-1.382-30.864,10.747-32.275,27.086c-0.383,4.448,2.91,8.367,7.359,8.75
    c4.444,0.382,8.367-2.91,8.75-7.359c0.643-7.463,7.283-13.002,14.785-12.369c3.641,0.312,6.94,2.016,9.289,4.797
    c2.343,2.773,3.461,6.287,3.15,9.895c-0.454,5.267-0.732,10.428-0.827,15.401c-0.059,5.237,0.069,10.421,0.383,15.454
    c0.751,10.99,2.389,21.246,4.869,30.498c3,11.1,7.041,20.472,12.049,27.91c3.104,4.535,6.399,8.157,9.795,10.769
    c2.107,1.677,4.166,2.783,5.907,3.644c0.115,0.058,0.234,0.112,0.352,0.165c2.073,0.904,4.254,1.545,6.685,1.957l1.328,0.223
    c1.066,0.181,2.178,1.103,2.242,2.52c0.041,0.908-0.357,1.541-0.698,1.913c-0.284,0.309-0.906,0.834-1.893,0.878
    c-3.125,0.142-6.203-0.126-9.13-0.789c-3.187-0.806-6.06-1.877-8.613-3.221c-4.917-2.524-9.461-6.011-14.273-10.946
    c-3.746-3.877-7.277-8.436-10.786-13.921c-3-4.715-5.714-9.736-8.288-15.337c-4.535-9.917-8.155-20.65-11.071-32.813
    c-1.04-4.341-5.401-7.023-9.745-5.977c-4.342,1.04-7.018,5.403-5.977,9.745c3.161,13.19,7.116,24.892,12.097,35.784
    c2.891,6.287,5.947,11.94,9.355,17.296c4.106,6.417,8.288,11.8,12.815,16.485c6.014,6.167,12.062,10.768,18.415,14.03
    c3.64,1.917,7.75,3.458,12.22,4.579c0.053,0.014,0.106,0.026,0.157,0.038c3.63,0.833,7.389,1.254,11.206,1.254
    c0.779,0,1.561-0.017,2.344-0.053c5.035-0.224,9.682-2.392,13.085-6.101c3.41-3.718,5.163-8.539,4.936-13.572
    c-0.396-8.8-6.993-16.258-15.701-17.735l-1.325-0.222c-1.037-0.177-1.94-0.427-2.754-0.766c-0.974-0.488-1.983-1.038-2.852-1.733
    c-0.047-0.038-0.095-0.076-0.143-0.112c-2.026-1.55-4.221-4.012-6.311-7.065c-3.977-5.909-7.281-13.663-9.813-23.03
    c-0.998-3.724-1.837-7.66-2.527-11.76c10.733-4.078,21.187-8.852,31.154-14.248c1.192-0.647,2.336-1.315,3.391-1.94
    c5.723-3.387,10.737-7.194,15.107-11.411c1.415,1.218,3.253,1.959,5.267,1.959c14.602,0,28.548-6.261,38.262-17.177l51.171-57.511
    c10.098-11.351,15.66-25.978,15.66-41.185C512,219.553,509.904,210.433,505.769,201.945z"
            />
            <circle fill="currentColor" cx="320.674" cy="229.052" r="16.168" />
            <circle fill="currentColor" cx="191.326" cy="229.052" r="16.168" />
            <path
                fill="currentColor"
                d="M354.322,113.446c-4.166-4.633-7.628-7.625-12.434-11.663c-11.441-9.611-23.999-17.114-37.31-22.416
			c-0.281-0.147-0.571-0.279-0.869-0.396c-3.573-1.394-7.203-2.617-10.877-3.686c0,0.005-0.001,0.011-0.001,0.015
			c-10.608-3.096-21.609-4.84-32.84-5.171l-0.124-0.004c-1.282-0.037-2.568-0.061-3.865-0.063c-0.001,0-0.001,0-0.002,0h-0.001
			c-0.001,0-0.002,0-0.003,0c-13.4,0.006-26.492,2.045-39.129,6.051c-0.129,0.041-0.26,0.082-0.389,0.123
			c-0.8,0.257-1.597,0.52-2.394,0.792c-0.221,0.075-0.442,0.154-0.663,0.231c-5.387,1.874-10.69,4.1-15.89,6.701
			c-14.26,7.131-27.394,17.004-39.035,29.341c-3.064,3.248-2.917,8.364,0.331,11.429s8.364,2.916,11.428-0.332
			c3.573-3.787,7.309-7.29,11.173-10.537c3.33,3.664,7.302,6.713,11.759,8.946c8.716,4.367,15.572,11.71,19.305,20.681
			c7.323,17.602,24.341,28.976,43.354,28.976h0.307c19.012,0,36.029-11.373,43.352-28.973c3.751-9.011,10.646-16.375,19.414-20.733
			c3.998-1.988,5.628-6.839,3.64-10.838c-1.987-3.998-6.835-5.629-10.838-3.64c-12.255,6.091-21.895,16.39-27.144,29
			c-4.806,11.552-15.964,19.016-28.424,19.016h-0.307c-12.462,0-23.62-7.465-28.426-19.019
			c-5.222-12.55-14.808-22.822-26.992-28.924c-2.157-1.081-4.122-2.486-5.853-4.14c3.322-2.108,6.714-4.053,10.188-5.789
			c2.01-1.006,4.038-1.949,6.08-2.831c14.138,7.976,25.253,20.452,31.505,35.475c2.291,5.504,7.588,9.06,13.497,9.06h0.307
			c5.909,0,11.207-3.555,13.497-9.059c6.276-15.082,17.444-27.587,31.656-35.558c10.723,4.589,20.868,10.829,30.182,18.653
			c4.412,3.706,7.34,6.236,10.812,10.096c1.596,1.774,3.8,2.677,6.014,2.677c1.925,0,3.859-0.684,5.404-2.074
			C357.038,121.876,357.308,116.766,354.322,113.446z M256,121.935c-5.898-12.855-14.763-24.053-25.767-32.742
			c5.805-1.354,11.706-2.24,17.683-2.662v2.395c0,4.466,3.62,8.084,8.084,8.084c4.466,0,8.084-3.619,8.084-8.084v-2.404
			c6.003,0.415,11.918,1.311,17.723,2.642C270.784,97.855,261.907,109.062,256,121.935z"
            />
            <path
                fill="currentColor"
                d="M189.964,310.487c7.503-0.636,14.142,4.906,14.787,12.369c0.384,4.447,4.279,7.73,8.75,7.359
			c4.447-0.384,7.743-4.302,7.359-8.75c-1.412-16.34-15.903-28.495-32.274-27.086c-7.934,0.68-15.129,4.399-20.26,10.473
			c-1.285,1.521-2.402,3.143-3.344,4.845c-2.354,4.255-3.616,9.01-3.673,13.914c-0.012,0.981,0.025,1.968,0.111,2.959v0.001
			c0.423,4.895,0.681,9.69,0.766,14.193c0.051,4.5-0.053,8.939-0.294,13.191c-8.703-3.477-17.19-7.444-25.327-11.848
			c-0.974-0.528-1.919-1.082-2.859-1.638c-35.644-21.096-31.653-59.263-23.918-99.839c0.836-4.385-2.042-8.619-6.427-9.454
			c-4.387-0.842-8.619,2.042-9.454,6.427c-4.079,21.397-7.415,43.975-4.237,64.132c1.559,9.888,4.576,18.665,9.066,26.431
			c-7.599-1.408-14.601-5.298-19.817-11.161l-51.172-57.51c-7.467-8.391-11.577-19.202-11.577-30.442
			c0-7.027,1.547-13.765,4.598-20.025l37.758-77.484c16.858-34.598,51.224-56.09,89.686-56.09c11.911,0,23.56,2.077,34.622,6.173
			c4.182,1.55,8.838-0.587,10.388-4.774c1.55-4.188-0.586-8.838-4.774-10.389c-12.865-4.763-26.403-7.179-40.236-7.179
			c-22.14,0-43.665,6.267-62.248,18.124C67.88,88.944,53.367,105.215,43.989,124.46L6.231,201.945C2.096,210.43,0,219.551,0,229.052
			c0,15.207,5.562,29.834,15.662,41.185l51.172,57.51c9.713,10.917,23.659,17.178,38.261,17.178c2.014,0,3.851-0.741,5.267-1.959
			c4.37,4.218,9.384,8.025,15.108,11.412c0.996,0.59,2.162,1.274,3.394,1.941c9.967,5.396,20.423,10.171,31.15,14.248
			c-0.69,4.099-1.525,8.028-2.521,11.743c-2.535,9.382-5.839,17.136-9.782,22.994c-2.126,3.105-4.321,5.566-6.348,7.118
			c-0.047,0.036-0.095,0.072-0.141,0.111c-0.869,0.696-1.877,1.246-2.852,1.734c-0.815,0.338-1.717,0.59-2.739,0.763l-1.351,0.227
			c-8.694,1.475-15.293,8.931-15.69,17.731c-0.227,5.035,1.526,9.855,4.937,13.573c3.403,3.709,8.05,5.877,13.083,6.101
			c0.785,0.036,1.566,0.053,2.346,0.053c3.816,0,7.575-0.42,11.206-1.254c0.053-0.012,0.106-0.024,0.157-0.038
			c4.469-1.121,8.58-2.662,12.144-4.54c6.43-3.302,12.479-7.902,18.518-14.096c4.497-4.654,8.679-10.038,12.8-16.476
			c3.399-5.343,6.456-10.995,9.35-17.293c4.974-10.877,8.929-22.577,12.09-35.77c1.037-4.343-1.64-8.706-5.981-9.747
			c-4.336-1.04-8.705,1.636-9.745,5.978c-2.915,12.165-6.536,22.898-11.065,32.8c-2.579,5.612-5.291,10.633-8.281,15.334
			c-3.524,5.505-7.056,10.064-10.771,13.911c-4.84,4.963-9.384,8.45-14.377,11.013c-2.478,1.306-5.35,2.377-8.538,3.182
			c-2.924,0.665-5.994,0.93-9.13,0.789c-0.985-0.044-1.608-0.57-1.892-0.88c-0.341-0.372-0.739-1.007-0.698-1.913
			c0.065-1.415,1.176-2.338,2.231-2.517l1.355-0.229c2.414-0.411,4.595-1.05,6.669-1.954c0.12-0.052,0.238-0.108,0.356-0.165
			c1.741-0.862,3.8-1.968,5.907-3.644c3.394-2.612,6.689-6.236,9.829-10.822c4.972-7.386,9.013-16.758,12.017-27.873
			c2.476-9.239,4.112-19.495,4.867-30.529c0.312-4.984,0.439-10.168,0.378-15.468c-0.094-4.924-0.371-10.085-0.825-15.34
			c-0.039-0.451-0.056-0.9-0.051-1.347c0.038-3.127,1.15-6.119,3.199-8.547C183.024,312.503,186.324,310.798,189.964,310.487z"
            />
            <path
                fill="currentColor"
                d="M288.337,328.397c-4.466,0-8.084,3.618-8.084,8.084v73.118l-9.162-12.216c-3.597-4.794-9.096-7.545-15.091-7.545
    s-11.495,2.751-15.091,7.545l-5.67,7.56c-2.679,3.572-1.954,8.639,1.617,11.318c3.571,2.679,8.639,1.954,11.318-1.617l5.67-7.56
    c0.703-0.938,1.652-1.078,2.156-1.078s1.453,0.14,2.156,1.078l21.41,28.548c-0.494,0.557-1.206,0.915-2.008,0.915h-43.116
    c-1.485,0-2.695-1.208-2.695-2.695v-97.371c0-4.466-3.62-8.084-8.084-8.084c-4.465,0-8.084,3.619-8.084,8.084v97.369
    c0,10.401,8.463,18.863,18.863,18.863h43.116c10.196,0,18.507-8.136,18.832-18.254c0.011-0.147,0.019-0.292,0.023-0.439
    c0-0.057,0.009-0.113,0.009-0.17v-97.37C296.421,332.016,292.803,328.397,288.337,328.397z"
            />
        </>
    ),
    6: (
        <g transform={`scale(${512 / 1800})`}>
            <path
                fill="currentColor"
                d="M886.355,871.67h53.495c10.102,0,18.296-8.19,18.296-18.292c0-10.102-8.194-18.297-18.296-18.297h-35.2
		v-85.604c0-10.104-8.188-18.294-18.294-18.294c-10.102,0-18.29,8.19-18.29,18.294v103.901
		C868.065,863.479,876.253,871.67,886.355,871.67z"
            />
            <path
                fill="currentColor"
                d="M1023.146,976.574c8.168-5.93,9.996-17.364,4.066-25.551c-5.922-8.177-17.373-10.006-25.551-4.066
		c-101.739,73.761-199.976,3.223-204.197,0.113c-8.101-6-19.54-4.328-25.57,3.771c-6.033,8.099-4.352,19.56,3.747,25.603
		c0.814,0.602,55.267,40.208,128.108,40.208C940.389,1016.651,981.693,1006.637,1023.146,976.574z"
            />
            <path
                fill="currentColor"
                d="M722.023,669.317c-21.604,0-39.12,24.242-39.12,54.165c0,29.916,17.517,54.171,39.12,54.171
		c21.606,0,39.121-24.256,39.121-54.171C761.145,693.56,743.629,669.317,722.023,669.317z"
            />
            <path
                fill="currentColor"
                d="M787.496,653.241c8.102,5.977,19.524,4.298,25.547-3.792c6.03-8.101,4.352-19.559-3.751-25.594
		c-29.785-22.174-104.559-50.742-174.351-0.139c-8.179,5.93-10.004,17.371-4.074,25.546c3.577,4.936,9.162,7.559,14.827,7.559
		c3.721,0,7.478-1.13,10.72-3.485C721.58,606.091,784.808,651.267,787.496,653.241z"
            />
            <path
                fill="currentColor"
                d="M1056.352,669.317c-21.615,0-39.127,24.242-39.127,54.165c0,29.916,17.512,54.171,39.127,54.171
		c21.597,0,39.118-24.256,39.118-54.171C1095.47,693.56,1077.948,669.317,1056.352,669.317z"
            />
            <path
                fill="currentColor"
                d="M990.863,653.241c2.665-1.942,65.915-47.15,131.09,0.094c3.248,2.355,7.001,3.485,10.719,3.485
		c5.67,0,11.252-2.623,14.831-7.559c5.931-8.175,4.11-19.616-4.075-25.546c-69.808-50.607-144.57-22.039-174.353,0.139
		c-8.082,6.016-9.745,17.411-3.763,25.516C971.295,657.476,982.738,659.191,990.863,653.241z"
            />
            <path
                fill="currentColor"
                d="M1044.421,1241.034v-73.482c144.013-55.917,249.028-190.594,261.882-350.876
		c54.584-8.121,96.594-55.29,96.594-112.094c0-52.986-36.558-97.585-85.777-109.929c9.814-23.683,17.381-48.378,22.441-73.552
		c22.416-111.207-3.248-225.034-72.289-320.511c-68.475-94.707-171.558-160.041-290.254-183.967
		c-32.013-6.448-64.617-9.723-96.897-9.723c-101.613,0-200.664,32.694-278.898,92.055
		c-79.953,60.67-134.599,146.725-153.866,242.316c-15.827,78.522-7.48,159.771,24.14,234.966l7.215,17.158
		c-52.042,10.239-91.424,56.193-91.424,111.192c0,57.297,42.746,104.765,98.016,112.271
		c13.167,163.313,122.008,300.019,270.279,353.863v70.313c-293.228,54.776-503.231,269.911-503.231,520.054v32.013h184.187v-64.025
		H317.726c13.115-155.446,121.035-292.501,281.221-370.137l163.441,370.137H604.229v-188.913v-1.533
		c0-16.833-14.332-30.479-32.015-30.479c-17.681,0-32.013,13.646-32.013,30.479v1.533v188.913v19.855v12.157v32.013h1007.448
		v-32.013C1547.649,1510.945,1337.645,1295.811,1044.421,1241.034z M930.688,1458.755l-21.867,237.567h-26.049l-21.862-237.567
		l34.886-28.459L930.688,1458.755z M797.781,1471.487l20.835,226.429l-84.098-190.454L797.781,1471.487z M993.72,1472.462l61.543,35
		l-81.808,185.281L993.72,1472.462z M1081.258,1448.584l-121.021-68.823l84.185-46.617v-26.892
		c30.759,6.287,60.437,14.517,88.879,24.479L1081.258,1448.584z M1338.872,704.582c0,20.803-12.967,38.599-31.229,45.833v-81.098
		v-10.562C1325.905,665.991,1338.872,683.786,1338.872,704.582z M510.122,353.917C543.173,189.945,698.781,70.929,880.12,70.929
		c28.048,0,56.385,2.843,84.252,8.462c102.856,20.733,192.005,77.098,251.014,158.712c58.452,80.838,80.267,176.85,61.413,270.353
		c-2.422,12.005-5.504,23.879-9.232,35.546c-74.684-38.646-176.441-81.161-283.574-95.821
		c-187.071-25.595-203.602-35.866-221.686-88.241l-15.614-45.238L710.842,346.4c-1.075,0.954-97.289,85.933-194.556,164.338
		C501.821,459.388,499.642,405.905,510.122,353.917z M451.313,704.587c0-21.33,13.638-39.487,32.631-46.347v11.077v81.614
		C464.951,744.071,451.313,725.916,451.313,704.587z M547.972,783.466V669.317V636.07v-68.66
		c67.086-53.777,134.851-111.775,173.301-145.113c36.729,56.681,103.2,68.686,254.046,89.32
		c104.311,14.275,205.434,59.292,268.301,92.136v32.317v33.247v114.148c0,191.785-156.039,347.824-347.824,347.824
		C704.004,1131.29,547.972,975.251,547.972,783.466z M895.795,1195.314c28.988,0,57.282-3.03,84.592-8.752v44.335v37.194v27.318
		l-80.547,44.613l-80.231-44.588v-27.344v-37.194v-42.681C844.309,1192.858,869.77,1195.314,895.795,1195.314z M755.582,1306.252
		v26.857l78.91,43.838l-125.97,71.637l-50.614-114.622C689.021,1322.563,721.623,1313.192,755.582,1306.252z M1249.584,1729.075
		v-190.446c0-16.833-14.334-30.479-32.012-30.479c-17.688,0-32.014,13.646-32.014,30.479v190.446h-158.163l165.243-374.213
		c164.844,76.87,276.295,216.041,289.637,374.213H1249.584z"
            />
        </g>
    ),
};

const AnimalDigitSvgContent = observer(function AnimalDigitSvgContent({
    digit,
    size,
    color,
    left = 0,
    top = 0,
}: DigitProps<NumberPTM>) {
    profiler.trace();

    return (
        <AutoSvg
            left={left - size / 2}
            top={top - size / 2}
            width={size}
            height={size}
            style={{ color }}
            scale={size / 512}
        >
            {svgMap[digit]}
        </AutoSvg>
    );
});

export const AnimalDigitComponentType: DigitComponentType<NumberPTM> = {
    component: AnimalDigit,
    svgContentComponent: AnimalDigitSvgContent,
    widthCoeff: 0.9,
};
// endregion

export const NationalGeographic: PuzzleDefinition<NumberPTM> = {
    noIndex: true,
    slug: "national-geographic",
    extension: {},
    author: Chameleon,
    title: {
        [LanguageCode.en]: "National Geographic",
    },
    typeManager: {
        ...DigitPuzzleTypeManager(AnimalDigitComponentType),
        digitShortcuts: [["H", "O"], ["D"], ["C"], ["M"], ["E"], ["P", "U"]],
        disableArrowLetterShortcuts: true,
        disableCellModeLetterShortcuts: true,
    },
    gridSize: GridSize6,
    regions: Regions6,
    rules: () => (
        <>
            <RulesParagraph>
                Totally normal 6x6 sudoku rules apply: put creatures available on the keypad into every row, column and
                3x2 box, once each.
            </RulesParagraph>
            <RulesParagraph>Horses cannot be orthogonally adjacent to dogs.</RulesParagraph>
            <RulesParagraph>Dogs cannot be orthogonally adjacent to cats.</RulesParagraph>
            <RulesParagraph>Cats cannot be orthogonally adjacent to mice.</RulesParagraph>
            <RulesParagraph>Mice cannot be orthogonally adjacent to elephants.</RulesParagraph>
            <RulesParagraph>Every dog must be orthogonally adjacent to at least one human.</RulesParagraph>
        </>
    ),
    initialDigits: {
        0: { 0: 3 },
        4: { 4: 4 },
    },
    items: [
        BaseNeighborsConstraint<NumberPTM>("Friendly animals", (a, b) => Math.abs(a - b) > 1 || a === 6 || b === 6),
        {
            name: "Dog",
            cells: [],
            props: undefined,
            isObvious: false,
            isValidCell: ({ top, left }, digits): boolean => {
                if (digits[top][left] !== 2) {
                    return true;
                }

                return [
                    { top: top + 1, left: left },
                    { top: top - 1, left: left },
                    { top: top, left: left + 1 },
                    { top: top, left: left - 1 },
                ].some(({ top, left }) => {
                    if (top < 0 || left < 0 || top >= 6 || left >= 6) {
                        return false;
                    }

                    const digit = digits[top]?.[left];
                    return digit === undefined || digit === 6;
                });
            },
        },
    ],
    resultChecker: isValidFinishedPuzzleByConstraints,
    allowDrawing: allDrawingModes,
};
